import { Spacing } from '@persona-id/design-tokens';
import { MoreIcon } from '@persona-id/design-tokens/icons';
import { sortBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import BankStatementBalances from 'dashboard/components/BankStatementVerification/BankStatementBalances';
import BankStatementDocumentInfo from 'dashboard/components/BankStatementVerification/BankStatementDocumentInfo';
import BankStatementTransactions from 'dashboard/components/BankStatementVerification/BankStatementTransactions';
import {
  BankStatementData,
  BankStatementDataField,
  DocumentGeneric,
} from 'dashboard/models/document';
import { Inquiry } from 'dashboard/models/inquiry';
import { VerificationDocument } from 'dashboard/models/verification';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { downloadData } from 'lib/download';
import { parseField } from 'lib/fields';

const ALL_STATEMENTS = -1;

enum BankStatementSection {
  All = 'all',
  DocumentInfo = 'document_info',
  Balances = 'balances',
  Transactions = 'transactions',
}

const FileNameParagraph = styled(Paragraph)``;

const StatementSelect = styled(Select)`
  width: 320px;

  ${FileNameParagraph} {
    display: none;
  }
`;

const ButtonGroup = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: ${Spacing[2]};
  grid-auto-columns: max-content;
  margin-bottom: ${Spacing[2]};
`;

const HiddenSection = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'unset' : 'none')};
`;

interface BankStatementVerificationProps {
  inquiry?: Inquiry;
  verifications: VerificationDocument[];
  documents: DocumentGeneric[];
  isRedacted?: boolean;
}

const buildBankNames = (statements: BankStatementData[]) => {
  const bankNamesToAttachmentIds = new Map<string, string[]>();
  statements?.forEach((statement) => {
    const bankName = statement?.['statement-info']?.['bank-name'] || 'Unknown';
    const attachmentId = statement?.['file-info']?.['attachment-id'];
    if (attachmentId) {
      bankNamesToAttachmentIds.set(bankName, [
        ...(bankNamesToAttachmentIds.get(bankName) || []),
        attachmentId,
      ]);
    }
  });

  const attachmentIdsToBankNames = new Map<string, string>();
  bankNamesToAttachmentIds.forEach((attachmentIds, bankName) => {
    if (attachmentIds.length === 1) {
      attachmentIdsToBankNames.set(attachmentIds[0], bankName);
    } else {
      attachmentIds.forEach((attachmentId, index) => {
        attachmentIdsToBankNames.set(attachmentId, bankName + ' ' + (index + 1));
      });
    }
  });

  return attachmentIdsToBankNames;
};

const buildAccountNumbers = (statements: BankStatementData[]) => {
  const attachmentIdsToAccountNumbers = new Map<string, string>();
  statements?.forEach((statement) => {
    const attachmentId = statement?.['file-info']?.['attachment-id'];
    const accountNumber = statement?.['statement-info']?.['account-number'];

    if (attachmentId && accountNumber) {
      attachmentIdsToAccountNumbers.set(attachmentId, accountNumber);
    }
  });

  return attachmentIdsToAccountNumbers;
};

const BankStatementVerification = ({
  inquiry,
  verifications,
  documents,
  isRedacted = false,
}: BankStatementVerificationProps) => {
  const [selectedStatement, setSelectedStatement] = useState(ALL_STATEMENTS);
  const [selectedSection, setSelectedSelection] = useState(BankStatementSection.All);

  const handleSelectedStatement = (indexStr: string) => {
    const index = Number(indexStr);
    if (index === ALL_STATEMENTS && selectedSection === BankStatementSection.DocumentInfo) {
      setSelectedSelection(BankStatementSection.All);
    }
    setSelectedStatement(index);
  };

  const statements: BankStatementData[] = useMemo(
    () =>
      documents
        .map(
          (document) =>
            parseField(document?.attributes?.fields?.[BankStatementDataField]) as BankStatementData,
        )
        .map((statement) => {
          const monthlyData = sortBy(['year', 'month'], statement?.['monthly-data']).map((md) => ({
            ...md,
            'financial-transactions': sortBy('date', md?.['financial-transactions']),
          }));

          return {
            ...statement,
            'monthly-data': monthlyData,
            'combined-data': {
              ...statement?.['combined-data'],
              'financial-transactions': monthlyData.flatMap((md) => md?.['financial-transactions']),
            },
          };
        }),
    [documents],
  );
  const bankNames = buildBankNames(statements);
  const accountNumbers = buildAccountNumbers(statements);
  const allStatements: BankStatementData = useMemo(() => {
    let statement = parseField(
      inquiry?.attributes?.fields?.[BankStatementDataField],
    ) as BankStatementData;
    if (statement == null && statements.length === 1) {
      statement = statements[0];
    }

    const monthlyData = sortBy(['year', 'month'], statement?.['monthly-data']).map((md) => ({
      ...md,
      'financial-transactions': sortBy('date', md?.['financial-transactions']).map(
        (transaction) => {
          const attachmentId =
            transaction?.['attachment-id'] || statement?.['file-info']?.['attachment-id'];
          return {
            ...transaction,
            document: attachmentId ? bankNames.get(attachmentId) : null,
            'account-number': attachmentId ? accountNumbers.get(attachmentId) : null,
          };
        },
      ),
    }));

    return {
      ...statement,
      'monthly-data': monthlyData,
      'combined-data': {
        ...statement?.['combined-data'],
        'financial-transactions': monthlyData.flatMap((md) => md?.['financial-transactions']),
      },
    };
  }, [inquiry, statements, bankNames, accountNumbers]);

  const isAll = selectedStatement === ALL_STATEMENTS;
  const statement = isAll ? allStatements : statements[selectedStatement];

  if (documents == null) {
    return null;
  }

  const getAttachmentId = (statement: BankStatementData) =>
    statement?.['file-info']?.['attachment-id'] || '';

  const bankStatementDataJsonDownload = () =>
    downloadData({
      data: JSON.stringify(allStatements),
      filename: 'bank_statement_analysis_data.json',
    });

  return (
    <Card>
      <Card.Body>
        <Paragraph color='primary'>Statement</Paragraph>
        <HStack>
          <StatementSelect
            value={selectedStatement.toString()}
            onValueChange={handleSelectedStatement}
            portal
          >
            <Option value={ALL_STATEMENTS.toString()}>
              <Paragraph color='primary'>All statements</Paragraph>
              <FileNameParagraph>Consolidated statements</FileNameParagraph>
            </Option>
            {!isRedacted &&
              statements?.map((statement, index) => (
                <Option
                  key={getAttachmentId(statement)}
                  value={index.toString()}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Paragraph color='primary'>
                    {getAttachmentId(statement)
                      ? bankNames.get(getAttachmentId(statement))
                      : 'Unknown'}
                  </Paragraph>
                  <FileNameParagraph>
                    {statement?.['file-info']?.['file-name'] || 'Unknown'}
                  </FileNameParagraph>
                </Option>
              ))}
          </StatementSelect>
          <DropdownMenu
            trigger={<Button variant='basic' width='auto' prefixIcon={<MoreIcon />}></Button>}
          >
            <DropdownMenu.Item
              id='bank-statement-verification__button-all'
              key='bank-statement-verification__button-all'
              variant='basic'
              disabled={false}
              onClick={bankStatementDataJsonDownload}
            >
              Download full analysis JSON
            </DropdownMenu.Item>
          </DropdownMenu>
        </HStack>
        <Spacer height={4} />
        <ButtonGroup>
          <Button
            id='bank-statement-verification__button-all'
            size='sm'
            width='auto'
            variant={selectedSection === BankStatementSection.All ? 'primary' : 'basic'}
            onClick={() => setSelectedSelection(BankStatementSection.All)}
          >
            All
          </Button>
          {!isAll && (
            <Button
              id='bank-statement-verification__button-document-info'
              size='sm'
              width='auto'
              variant={selectedSection === BankStatementSection.DocumentInfo ? 'primary' : 'basic'}
              onClick={() => setSelectedSelection(BankStatementSection.DocumentInfo)}
            >
              Document info
            </Button>
          )}
          <Button
            id='bank-statement-verification__button-balances'
            size='sm'
            width='auto'
            variant={selectedSection === BankStatementSection.Balances ? 'primary' : 'basic'}
            onClick={() => setSelectedSelection(BankStatementSection.Balances)}
          >
            Balances
          </Button>
          <Button
            id='bank-statement-verification__button-transactions'
            size='sm'
            width='auto'
            variant={selectedSection === BankStatementSection.Transactions ? 'primary' : 'basic'}
            onClick={() => setSelectedSelection(BankStatementSection.Transactions)}
          >
            Transactions
          </Button>
        </ButtonGroup>
        {!isAll && (
          <HiddenSection
            visible={
              selectedSection === BankStatementSection.All ||
              selectedSection === BankStatementSection.DocumentInfo
            }
          >
            <BankStatementDocumentInfo
              statement={statement}
              verification={verifications?.find(
                (v) => v.relationships.document?.data.id === documents?.[selectedStatement]?.id,
              )}
              document={documents[selectedStatement]}
              isRedacted={isRedacted}
            />
          </HiddenSection>
        )}
        <HiddenSection
          visible={
            selectedSection === BankStatementSection.All ||
            selectedSection === BankStatementSection.Balances
          }
        >
          <BankStatementBalances statement={statement} isRedacted={isRedacted} />
        </HiddenSection>
        <HiddenSection
          visible={
            selectedSection === BankStatementSection.All ||
            selectedSection === BankStatementSection.Transactions
          }
        >
          <BankStatementTransactions statement={statement} isRedacted={isRedacted} isAll={isAll} />
        </HiddenSection>
      </Card.Body>
    </Card>
  );
};

export default BankStatementVerification;
